import { Link } from 'react-router';
import { useState } from 'react';
import type { EstudioEnviado } from '../../../common/services/estudios.js';
import type { Idioma } from '../../../i18n/index.js';
import { textosEstudios } from '../textos.js';

export const BOTON_ESTUDIOS =
  'w-fit cursor-pointer rounded-full border border-(--line-strong) px-4 py-2 text-(--ink) hover:bg-(--chat-bg) disabled:cursor-default disabled:opacity-50';
export const CAMPO_ESTUDIOS = 'w-full rounded-lg border border-(--line-strong) bg-(--paper) p-3 text-(--ink)';

const TONOS_ESTADO = {
  verde: { borde: 'border-l-green-500', etiqueta: 'border-green-300 bg-green-100 text-green-900' },
  naranja: { borde: 'border-l-orange-500', etiqueta: 'border-orange-300 bg-orange-100 text-orange-900' },
  rojo: { borde: 'border-l-red-500', etiqueta: 'border-red-300 bg-red-100 text-red-900' },
  neutro: { borde: 'border-l-(--line-strong)', etiqueta: 'border-(--line-strong) bg-(--chat-bg) text-(--ink-soft)' },
};
const ETIQUETA_ESTADO = 'inline-flex w-fit rounded-full border px-3 py-1 text-sm font-semibold';

export function ListaEstudios({
  filas,
  idioma,
  admin = false,
  ocupado,
  ejecutar,
}: {
  readonly filas: readonly EstudioEnviado[];
  readonly idioma: Idioma;
  readonly admin?: boolean;
  readonly ocupado: boolean;
  readonly ejecutar: (id: string, accion: 'pdf' | 'retirar' | 'aprobar' | 'rechazar', motivo?: string) => void;
}) {
  const t = textosEstudios(idioma);
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  if (filas.length === 0) return <p>{t.vacio}</p>;
  return (
    <ul className='m-0 flex list-none flex-col gap-4 p-0'>
      {filas.map(f => {
        const estado = f.estado === 'aprobado' && f.estadoIngesta ? f.estadoIngesta : f.estado;
        const etiqueta = {
          enviado: t.pendiente,
          aprobado: t.aprobado,
          rechazado: t.rechazado,
          retirado: t.retirado,
          en_cola: t.colaProcesamiento,
          procesando: t.procesando,
          revision_tecnica: t.revision_tecnica,
          error: t.errorTecnico,
          incorporado: t.incorporado,
          cargado: t.cargado,
        }[estado];
        const tono =
          estado === 'rechazado' || estado === 'error'
            ? 'rojo'
            : estado === 'aprobado' || estado === 'incorporado'
              ? 'verde'
              : estado === 'retirado'
                ? 'neutro'
                : 'naranja';
        const procesamiento = f.estado === 'aprobado' && f.estadoIngesta !== null;
        return (
          <li key={f.id} className={`min-w-0 rounded-xl border border-(--line-strong) border-l-4 p-5 ${TONOS_ESTADO[tono].borde}`}>
            <h3 className='m-0 text-lg font-semibold break-words'>{f.titulo}</h3>
            <p className='m-0 mt-1 break-words'>
              {f.editor}
              {admin && f.email ? ` · ${f.email}` : ''}
            </p>
            <div className='mt-3 flex flex-wrap gap-2'>
              {procesamiento ? <span className={`${ETIQUETA_ESTADO} ${TONOS_ESTADO.verde.etiqueta}`}>{t.aprobado}</span> : null}
              <span className={`${ETIQUETA_ESTADO} ${TONOS_ESTADO[tono].etiqueta}`}>{etiqueta}</span>
            </div>
            <p className='m-0 mt-3 text-sm text-(--ink-soft)'>
              {new Date(f.creadoEn).toLocaleDateString(idioma)} · {(f.bytes / 1024 / 1024).toFixed(1)} MiB
            </p>
            {f.motivo ? <p className='m-0 mt-3 break-words'>{f.motivo}</p> : null}
            {admin && f.duplicado ? <p className='m-0 mt-3'>{t.duplicado}</p> : null}
            <div className='mt-5 flex flex-wrap gap-3'>
              <a href={f.url} target='_blank' rel='noopener noreferrer' className={BOTON_ESTUDIOS}>
                {t.url}
              </a>
              <button
                className={BOTON_ESTUDIOS}
                disabled={ocupado}
                type='button'
                onClick={() => {
                  ejecutar(f.id, 'pdf');
                }}
              >
                {t.verPdf}
              </button>
              {estado === 'incorporado' && f.fuenteSlug ? (
                <Link className={BOTON_ESTUDIOS} to={`/fuentes/${encodeURIComponent(f.fuenteSlug)}`}>
                  {t.ficha}
                </Link>
              ) : null}
              {!admin && f.estado === 'enviado' ? (
                <button
                  type='button'
                  disabled={ocupado}
                  className={BOTON_ESTUDIOS}
                  onClick={() => {
                    ejecutar(f.id, 'retirar');
                  }}
                >
                  {t.retirar}
                </button>
              ) : null}
              {admin && f.estado === 'enviado' ? (
                <button
                  type='button'
                  disabled={ocupado}
                  className={BOTON_ESTUDIOS}
                  onClick={() => {
                    ejecutar(f.id, 'aprobar');
                  }}
                >
                  {t.aprobar}
                </button>
              ) : null}
            </div>
            {admin && f.estado === 'enviado' ? (
              <form
                className='mt-4 flex flex-col gap-2'
                onSubmit={e => {
                  e.preventDefault();
                  ejecutar(f.id, 'rechazar', motivos[f.id]?.trim());
                }}
              >
                <label htmlFor={`motivo-${f.id}`}>{t.motivo}</label>
                <textarea
                  id={`motivo-${f.id}`}
                  className={CAMPO_ESTUDIOS}
                  required
                  maxLength={2000}
                  disabled={ocupado}
                  value={motivos[f.id] ?? ''}
                  onChange={e => {
                    setMotivos(m => ({ ...m, [f.id]: e.target.value }));
                  }}
                />
                <button className={BOTON_ESTUDIOS} disabled={ocupado || !motivos[f.id]?.trim()} type='submit'>
                  {t.rechazar}
                </button>
              </form>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
